import React, { useCallback, useEffect, useMemo, useState, lazy, Suspense } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DescriptionRounded,
  DeleteOutlineRounded,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import SubmittedReconciliationDialog from './SubmittedReconciliationDialog';
import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../contexts/PlatformContext';
import { recordDateKey, tradingDay } from '../utils/week';
import {
  dailySalesAPI,
  goldenLinesAPI,
  reconciliationAPI,
  sitesAPI,
  usersAPI,
} from '../services/api';

/*
 * Loaded on demand.
 *
 * This is the only remaining eager importer of recharts, which is 813 KB of
 * source — the second largest package in the bundle after MUI. Splitting it
 * keeps the charting library out of the initial download for every user,
 * including the site managers who make up most of them.
 *
 * It renders below the tiles and the records panel, so the page above it paints
 * while this arrives.
 */
const SmartOperationsDashboard = lazy(() => import('./SmartOperationsDashboard'));

import DashboardMasthead from './dashboard/DashboardMasthead';
import PriorityPanel from './dashboard/PriorityPanel';
import {
  IssuesPanel,
  PastReconciliationsPanel,
  SiteDirectoryPanel,
} from './dashboard/SiteRecordsPanels';

const isoDate = (date) => {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
};

const getWeekForDate = (input = new Date()) => {
  const today = new Date(input);
  const day = today.getDay();
  const daysSinceMonday = (day + 6) % 7;
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  start.setDate(today.getDate() - daysSinceMonday);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return { start: isoDate(start), end: isoDate(end) };
};

const emptyTotals = {
  shopSalesTotal: 0,
  fuelLitres: {},
  solar: { sum: 0 },
  daysFilled: 0,
};

const totalsFrom = (response) =>
  response?.data?.data?.totals || response?.data?.totals || emptyTotals;

const subtractTotals = (current, previous) => {
  const fuelKeys = new Set([
    ...Object.keys(current?.fuelLitres || {}),
    ...Object.keys(previous?.fuelLitres || {}),
  ]);
  const fuelLitres = {};
  fuelKeys.forEach((key) => {
    fuelLitres[key] = Number(current?.fuelLitres?.[key] || 0) - Number(previous?.fuelLitres?.[key] || 0);
  });
  return {
    shopSalesTotal: Number(current?.shopSalesTotal || 0) - Number(previous?.shopSalesTotal || 0),
    fuelLitres,
    solar: {
      sum: Number(current?.solar?.sum || 0) - Number(previous?.solar?.sum || 0),
    },
  };
};

const formatWeek = (start, end) => {
  const format = (value) =>
    new Date(`${value}T12:00:00`).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
    });
  return `${format(start)} – ${format(end)}`;
};

const SITE_DIRECTORY_DETAILS = {
  ACKWORTH: {
    managerName: 'Yannis Portel',
    email: 'valliforecourts.ackworth@googlemail.com',
    phone: '01977 611832',
  },
  BRAMALL: {
    managerName: 'Mr Tauqeer',
    email: 'valliforecourts.bramall@gmail.com',
    phone: '0114 275 7527',
  },
  BROADWAY: {
    managerName: 'Miss Pavla',
    email: 'valliforecourts.broadway@gmail.com',
    phone: '01765 609572',
  },
  CANALROAD: {
    managerName: 'Amrik Singh',
    email: 'vf.canalroad@gmail.com',
    phone: '01274 053935',
  },
  CIVIC: {
    managerName: 'Muhammad Zunair',
    email: 'vf.civicss@gmail.com',
    phone: '01274 020042',
  },
  FALLINGS: {
    managerName: 'Adeel Javed',
    email: 'vf.fallings@gmail.com',
    phone: '01924 900600',
  },
  GLEDHOW: {
    managerName: 'Nesar Hussain',
    email: 'valliforecourts.gledhow@googlemail.com',
    phone: '0113 235 1245',
  },
  MANOR: {
    managerName: 'Syed Khurram',
    email: 'valliforecourts.manor@gmail.com',
    phone: '01924 822944',
  },
  MOORTOWN: {
    managerName: 'Anas Patel',
    email: 'valliforecourts.moortown@gmail.com',
    phone: '0113 268 8370',
  },
  RACECOURSE: {
    managerName: 'Mr Andy',
    email: 'valliforecourts.racecourse@gmail.com',
    phone: '01302 279723',
  },
  RELIANCE: {
    managerName: 'Mr Usman',
    email: 'valliforecourts.reliance@gmail.com',
    phone: '01642 673832',
  },
  STANLEY: {
    managerName: 'Mr Umair',
    email: 'vf.stanleyss@gmail.com',
    phone: '01207 811273',
  },
  STCHADS: {
    managerName: 'Rashid Iqbal',
    email: 'valliforecourts.stchads@gmail.com',
    phone: '0113 278 1171',
  },
  WHITECROSS: {
    managerName: 'Mr Irfan',
    email: 'valliforecourts.whitecross@gmail.com',
    phone: '01943 873666',
  },
  YORK: {
    managerName: 'Mr Dilshad',
    email: 'valliforecourts.york@gmail.com',
    phone: '01904 870982',
  },
  CLOCK: {
    managerName: 'Mr Kashif',
    email: 'valliforecourts.clock@gmail.com',
    phone: '0121 725 3764',
  },
  HAWTHORNES: {
    managerName: 'Mr Nabeel',
    email: 'valliforecourts.hawthornes@gmail.com',
    phone: '0121 524 1160',
  },
  TAMWORTH: {
    managerName: 'Mr Masud',
    email: 'valliforecourts.tamworth@gmail.com',
    phone: '01827 946630',
  },
};

const reconciliationDate = (row) =>
  String(row?.businessDate || row?.date || row?.createdAt || '').slice(0, 10);

/**
 * The unexplained cash on a day, in pounds.
 *
 * Same precedence as hasOpenIssue, which decides whether a day counts as an
 * issue at all — read from one place so the list cannot say a day is a problem
 * while printing a figure that is under the threshold.
 */
const openVarianceOf = (row) =>
  Number(
    row?.issueSummary?.cashVarianceExcludingSubway ??
      row?.cashVarianceExcludingSubway ??
      row?.variances?.cashVariance ??
      row?.shopCashVariance ??
      0
  );

const hasOpenIssue = (row) => {
  const resolved = Boolean(
    row?.issueResolution?.isResolved ||
    row?.issueSummary?.isResolved ||
    row?.issueSummary?.resolved
  );
  if (resolved) return false;
  if (row?.issueSummary?.isIssue === true) return true;
  const variance = Number(
    row?.issueSummary?.cashVarianceExcludingSubway ??
    row?.cashVarianceExcludingSubway ??
    row?.variances?.cashVariance ??
    row?.shopCashVariance ??
    0
  );
  return Math.abs(variance) > 15;
};

const hadIssue = (row) => {
  if (row?.issueSummary?.isIssue === true) return true;
  const variance = Number(
    row?.issueSummary?.cashVarianceIncludingSubway ??
    row?.cashVarianceIncludingSubway ??
    row?.issueSummary?.cashVarianceExcludingSubway ??
    row?.cashVarianceExcludingSubway ??
    row?.variances?.cashVariance ??
    row?.shopCashVariance ??
    0
  );
  return Math.abs(variance) > 15;
};

/**
 * Why a cash variance happened. Mirrors ISSUE_CATEGORY_LABELS on the
 * Reconciliation model — the backend rejects anything not in this list.
 */
const ISSUE_CATEGORY_OPTIONS = [
  { value: 'till_miscount', label: 'Till miscount' },
  { value: 'banking_error', label: 'Banking error' },
  { value: 'pouch_discrepancy', label: 'Pouch discrepancy' },
  { value: 'drive_off_nmop', label: 'Drive off / NMOP not recorded' },
  { value: 'paid_in_out_not_recorded', label: 'Paid in/out not recorded' },
  { value: 'cash_machine_error', label: 'Cash machine / Volumatic error' },
  { value: 'refund_void_error', label: 'Refund or void error' },
  { value: 'under_investigation', label: 'Under investigation' },
  { value: 'written_off', label: 'Written off (authorised)' },
  { value: 'other', label: 'Other' },
];

const categoryLabel = (value) =>
  ISSUE_CATEGORY_OPTIONS.find((o) => o.value === value)?.label || value || '';

/**
 * Sign-off form for an open variance.
 *
 * A variance is closed by an authorisation, not a tick — so the cause, what
 * happened, the action taken and who authorised it are all required. The
 * backend enforces the same four, so a partial submission is rejected there
 * too rather than silently saving half a record.
 */
function ResolveIssueForm({ row, variance, onResolved }) {
  const [form, setForm] = useState({
    category: '',
    reason: '',
    actionTaken: '',
    authorisedByName: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const incomplete =
    !form.category ||
    !form.reason.trim() ||
    !form.actionTaken.trim() ||
    !form.authorisedByName.trim();

  const submit = async () => {
    if (incomplete || saving) return;

    try {
      setSaving(true);
      setError('');
      await reconciliationAPI.resolveIssue(row?._id, {
        category: form.category,
        reason: form.reason.trim(),
        actionTaken: form.actionTaken.trim(),
        authorisedByName: form.authorisedByName.trim(),
      });
      onResolved?.();
    } catch (e) {
      const data = e?.response?.data;
      setError(
        (data?.details && data.details.join('. ')) ||
          data?.message ||
          'Could not resolve this issue.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 1.25, p: 1.5, borderRadius: 2, bgcolor: '#fdf6ee', border: '1px solid #f0d9bd' }}>
      <Typography variant="body2" sx={{ fontWeight: 800, mb: 1.25 }}>
        Resolve this variance of{' '}
        {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(variance)}
      </Typography>

      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            size="small"
            required
            label="What caused it?"
            value={form.category}
            onChange={set('category')}
          >
            {ISSUE_CATEGORY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            required
            label="Authorised by"
            placeholder="Name of the person signing this off"
            value={form.authorisedByName}
            onChange={set('authorisedByName')}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            required
            multiline
            minRows={2}
            label="What happened?"
            placeholder="e.g. Till 2 afternoon pouch was counted into Till 1"
            value={form.reason}
            onChange={set('reason')}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            size="small"
            required
            multiline
            minRows={2}
            label="Action taken"
            placeholder="e.g. Pouch reallocated and cashier retrained on the count sheet"
            value={form.actionTaken}
            onChange={set('actionTaken')}
          />
        </Grid>
      </Grid>

      {error ? (
        <Alert severity="error" sx={{ mt: 1.25 }}>
          {error}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} alignItems="center">
        <Button variant="contained" size="small" disabled={incomplete || saving} onClick={submit}>
          {saving ? 'Saving…' : 'Resolve issue'}
        </Button>
        {incomplete ? (
          <Typography variant="caption" color="text.secondary">
            All four fields are required.
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}

const resolutionDetails = (row) => ({
  resolved: Boolean(
    row?.issueResolution?.isResolved ||
    row?.issueSummary?.isResolved ||
    row?.issueSummary?.resolved
  ),
  reason:
    row?.issueResolution?.reason ||
    row?.issueSummary?.issueResolution?.reason ||
    row?.issueSummary?.resolutionReason ||
    '',
  person:
    row?.issueResolution?.resolvedByName ||
    row?.issueSummary?.issueResolution?.resolvedByName ||
    row?.issueSummary?.resolvedByName ||
    '',
  date:
    row?.issueResolution?.resolvedAt ||
    row?.issueSummary?.issueResolution?.resolvedAt ||
    row?.issueSummary?.resolvedAt ||
    '',
});

/**
 * A dashboard card.
 *
 * `attention` raises the card when something needs doing — a tinted ground and
 * a coloured top rule, so an outstanding item is distinguishable from a settled
 * one across the room rather than only by reading the number.
 *
 * `statusText` replaces the numeral for cards that report a state rather than a
 * count. A count of zero and a state of "done" look identical as a grey 0, and
 * they mean opposite things.
 */
/**
 * The seven days of the selected week, marked by whether a Daily Sales record
 * exists for each.
 *
 * This sits in the middle of the page header, which was a thousand pixels of
 * nothing between the title and the week control. Nowhere else on the page says
 * WHICH days are missing — the alert gives a count, the status panel gives a
 * percentage — so finding the gap meant opening Daily Sales and reading the
 * list. Each day is a link straight to itself.
 */
function WeekUploadStrip({ weekStart, uploadedDays, dueKey, loading, onOpenDay }) {
  const days = [];
  const cursor = new Date(`${weekStart}T12:00:00`);

  for (let index = 0; index < 7; index += 1) {
    const key = isoDate(cursor);

    days.push({
      key,
      letter: cursor.toLocaleDateString('en-GB', { weekday: 'narrow' }),
      number: cursor.getDate(),
      uploaded: uploadedDays.has(key),
      /*
       * Three states, not two.
       *
       * A day is filed the morning after it trades, so today is not due
       * yet, yesterday is due now, and anything older is late. Counting
       * today as due made every day of the week end "1 of 2 filed" with an
       * amber pill that was never going to go green until tomorrow.
       */
      isDueToday: key === dueKey,
      isFuture: key > dueKey,
    });

    cursor.setDate(cursor.getDate() + 1);
  }

  const filed = days.filter((day) => day.uploaded).length;
  const due = days.filter((day) => !day.isFuture).length;
  const late = days.filter((day) => !day.uploaded && !day.isFuture && !day.isDueToday).length;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.75 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 850, lineHeight: 1 }}>
          Daily sales
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 800, color: filed >= due ? '#07883f' : late ? '#b3261e' : '#d97706' }}
        >
          {loading ? '—' : `${filed} of ${due} filed`}
          {!loading && late ? ` · ${late} late` : ''}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={0.5}>
        {days.map((day) => {
          // Green when it is in; amber when it is this morning's job; red
          // when it should have been done before today; faint when the day
          // has not happened yet, or has not finished trading.
          const tone = day.uploaded
            ? '#07883f'
            : day.isFuture
            ? '#cbd5e1'
            : day.isDueToday
            ? '#d97706'
            : '#b3261e';
          const solid = day.uploaded;

          return (
            <Tooltip
              key={day.key}
              title={`${new Date(`${day.key}T12:00:00`).toLocaleDateString('en-GB', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })} — ${
                day.uploaded
                  ? 'filed'
                  : day.isFuture
                  ? 'not due yet'
                  : day.isDueToday
                  ? 'due this morning'
                  : 'late'
              }`}
            >
              <Box
                role="button"
                tabIndex={0}
                onClick={() => onOpenDay(day.key)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onOpenDay(day.key);
                  }
                }}
                sx={{
                  width: 38,
                  py: 0.6,
                  borderRadius: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  userSelect: 'none',
                  border: '1px solid',
                  borderColor: solid ? tone : `${tone}66`,
                  bgcolor: solid ? `${tone}14` : '#fff',
                  outline: day.isDueToday ? `2px solid ${tone}55` : 'none',
                  outlineOffset: 1,
                  transition: 'border-color .16s ease, background-color .16s ease',
                  '&:hover': { borderColor: tone, bgcolor: `${tone}1A` },
                }}
              >
                <Typography sx={{ fontSize: 10, fontWeight: 850, lineHeight: 1.1, color: 'text.secondary' }}>
                  {day.letter}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 950, lineHeight: 1.2, color: day.isFuture ? '#94a3b8' : tone }}>
                  {day.number}
                </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Stack>
    </Box>
  );
}

/* FollowUpCard lived here. The four status cards it drew — reconciliation,
   drafts, issues and a count of submitted reports — are the "What needs
   doing" list now, so nothing renders it any more. */

/*
 * The job list, a slot, and the records.
 *
 * The page needs the week's figures and the fuel cover to sit between what is
 * outstanding and what has already been filed — and both of those are drawn by
 * the operations dashboard, which is lazily loaded and lives one level up. The
 * alternative was fetching this component's data twice so it could be rendered
 * either side of it, which would double the load on the reconciliation list
 * endpoint to arrange two panels on a page.
 */
function RecordsAndFollowUp({ siteId, siteCode, refreshKey, uploadedDays, onDayStatus, middle }) {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [sites, setSites] = useState([]);
  const [users, setUsers] = useState([]);

const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [issueMonth, setIssueMonth] = useState(new Date().toISOString().slice(0, 7));
  const [discardingId, setDiscardingId] = useState('');

  const navigateToReconciliation = () =>
    navigate(`/platform/site/${siteCode}/reconciliation`);

  // Bumped after an issue is resolved or reopened so the list reloads in place.
  const [issueRefresh, setIssueRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.allSettled([
      /*
       * summary=1: the fields this screen actually reads.
       *
       * The full list was 1,426 KB of documents for one site — every till,
       * every shift, every uploaded file reference — to render four counts and
       * a list of dates. The endpoint has served a 90 KB projection since the
       * performance work; nothing had been pointed at it.
       *
       * The full document is fetched on demand when a row is opened, which is
       * the only place it is needed.
       */
      reconciliationAPI.getAll({ site: siteId, page: 1, limit: 1000, summary: 1 }),
      sitesAPI.getAll(),
      usersAPI.directory(),
    ]).then(([recordResult, siteResult, userResult]) => {
      if (!active) return;
      setRecords(
        recordResult.status === 'fulfilled' && Array.isArray(recordResult.value?.data?.data)
          ? recordResult.value.data.data
          : []
      );
      setSites(
        siteResult.status === 'fulfilled' && Array.isArray(siteResult.value?.data?.data)
          ? siteResult.value.data.data.filter((site) => site?.active !== false)
          : []
      );
      setUsers(
        userResult.status === 'fulfilled' && Array.isArray(userResult.value?.data?.data)
          ? userResult.value.data.data
          : []
      );
      setLoading(false);
    });
    return () => { active = false; };
  }, [refreshKey, siteId, issueRefresh]);

  const submitted = records.filter((row) =>
    ['submitted', 'approved', 'rejected'].includes(String(row?.status || '').toLowerCase())
  );
  const drafts = records.filter((row) => String(row?.status || '').toLowerCase() === 'draft');
  const issues = submitted.filter(hasOpenIssue);

  /*
   * The state of the reconciliation being filed this morning.
   *
   * This is the question the page exists to answer and it was previously only
   * reachable by scrolling past a chart to the "Reconciliation Status" panel,
   * which reports a weekly percentage rather than today.
   */
  /*
   * The reconciliation this morning is for.
   *
   * Banking is done a day behind, so at nine on a Tuesday the job is
   * Monday's - and this card, which is the reason the page exists, was
   * reporting on Tuesday: permanently "Not started", with a Start now
   * button that opened a day nobody was working on yet.
   */
  const dueKey = tradingDay();
  const dueLabel = new Date(`${dueKey}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  /* "Monday's reconciliation" beats "Today's reconciliation" when it is
     Tuesday and the record is Monday's. */
  const dueWeekday = new Date(`${dueKey}T12:00:00`).toLocaleDateString('en-GB', {
    weekday: 'long',
  });

  /*
   * Two dates, and never one standing in for the other.
   *
   * This heading read "This morning — Thursday 10 September" on a Friday.
   * Both halves were true and together they said the wrong thing: the only
   * date on the page was the one being filed, sitting directly under a word
   * that means now. Today is named, the day being worked on is named, and
   * which is which is said in the sentence rather than left to be worked
   * out.
   */
  const todayLabel = new Date().toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const dueRecord = records.find((row) => reconciliationDate(row) === dueKey);
  const dueState = String(dueRecord?.status || '').toLowerCase();

  const dueStatus = (() => {
    if (dueState === 'submitted' || dueState === 'approved') {
      return {
        label: 'Submitted',
        helper: dueRecord?.submittedByName
          ? `Banked by ${dueRecord.submittedByName}`
          : 'Nothing further needed today',
        tone: 'success',
        attention: false,
        onClick: () => openRecord(dueRecord),
      };
    }

    if (dueState === 'draft') {
      return {
        label: 'In progress',
        helper: 'Saved but not submitted — open to finish',
        tone: 'warning',
        attention: true,
        onClick: navigateToReconciliation,
      };
    }

    if (dueState === 'rejected') {
      return {
        label: 'Rejected',
        helper: 'Returned for correction — open to amend',
        tone: 'error',
        attention: true,
        onClick: () => openRecord(dueRecord),
      };
    }

    return {
      label: 'Not started',
      helper: 'Start today’s banking and cash count',
      tone: 'warning',
      attention: true,
      onClick: navigateToReconciliation,
    };
  })();

  /*
   * The one thing to do next, named by date.
   *
   * The cards above report counts — "2 pending", "1 to resolve" — but never
   * which day, so finding the work meant opening a dialog and scanning it. The
   * Reconciliation Status panel already computed similar guidance, at the very
   * bottom of the page where nobody reached it.
   *
   * Ordered by cost of leaving it: an unresolved cash variance ages badly, a
   * half-finished reconciliation blocks the day's banking, and today's count is
   * the routine job.
   */
  /*
   * Everything outstanding, in the order it should be dealt with.
   *
   * The page used to tell the same story five times over: the day pills at
   * the top ("0 of 1 filed"), a card for the reconciliation, a card for
   * drafts, a card for issues, a Next action banner repeating whichever of
   * those was most urgent, and an alert repeating it again. Six surfaces,
   * one fact, and a manager still had to work out what to press.
   *
   * It is a list of jobs. Ordered by what it costs to leave it: a cash
   * variance ages badly and someone has to sign it off, a half-finished
   * reconciliation blocks the banking, and the day's own filing is the
   * routine work. When there is nothing on it, that is worth saying plainly
   * rather than drawing four cards that all say "All clear".
   */
  const oldestBy = (rows) =>
    [...rows].sort((a, b) => reconciliationDate(a).localeCompare(reconciliationDate(b)))[0];

  const namedDay = (row) => {
    const key = reconciliationDate(row);
    if (!key) return 'an earlier day';
    return new Date(`${key}T12:00:00`).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const buildOutstanding = () => {
    const jobs = [];

    if (issues.length) {
      /*
       * The money, on the line that asks for it.
       *
       * "3 cash variances are waiting for sign-off" gives a manager no way to
       * decide whether to do it now or after the delivery. £412 does.
       */
      const exposure = issues.reduce((sum, row) => sum + Math.abs(openVarianceOf(row)), 0);
      const oldest = oldestBy(issues);

      jobs.push({
        key: 'issues',
        text:
          issues.length === 1
            ? 'One cash variance is waiting for sign-off'
            : `${issues.length} cash variances are waiting for sign-off`,
        detail: oldest ? `Oldest is ${namedDay(oldest)}` : undefined,
        value: exposure ? `£${exposure.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : undefined,
        cta: 'Review',
        tone: 'error',
        onClick: () => setDialog('issues'),
      });
    }

    if (drafts.length) {
      jobs.push({
        key: 'drafts',
        text:
          drafts.length === 1
            ? 'A reconciliation is part-finished'
            : `${drafts.length} reconciliations are part-finished`,
        detail: (() => {
          const oldest = oldestBy(drafts);
          return oldest ? `Started on ${namedDay(oldest)} and not submitted` : undefined;
        })(),
        cta: 'Continue',
        tone: 'warning',
        onClick: () => setDialog('drafts'),
      });
    }

    if (dueStatus.label !== 'Submitted' && dueStatus.label !== 'Approved') {
      jobs.push({
        key: 'reconciliation',
        text: `${dueWeekday}'s banking and cash count ${
          dueStatus.label === 'Draft' ? 'is part-finished' : 'has not been started'
        }`,
        detail: 'The pack does not go to the directors until this is submitted',
        cta: dueStatus.label === 'Draft' ? 'Continue' : 'Start',
        tone: 'warning',
        onClick: dueStatus.onClick || navigateToReconciliation,
      });
    }

    if (uploadedDays && !uploadedDays.has(dueKey)) {
      jobs.push({
        key: 'sales',
        text: `${dueWeekday}'s daily sales have not been uploaded`,
        detail: 'Shop, fuel and the delivery platforms all come from this file',
        cta: 'Upload',
        tone: 'warning',
        onClick: () =>
          navigate(`/platform/site/${siteCode}/daily-sales?start=${dueKey}&end=${dueKey}`),
      });
    }

    return jobs;
  };

  /*
   * The jobs, shaped for one ordered list.
   *
   * A job that stops the day being filed is urgent; one that will bite later
   * in the week is not, and printing them at the same weight was what made the
   * old list impossible to triage.
   */
  const priorityItems = useMemo(() => {
    const toneFor = (job) => (job.tone === 'error' ? 'urgent' : 'soon');

    return buildOutstanding().map((job) => ({
      key: job.key,
      tone: toneFor(job),
      title: job.text,
      detail: job.detail,
      value: job.value,
      cta: job.cta,
      onClick: job.onClick,
    }));
    /* Deliberate deps: buildOutstanding closes over exactly these. */
  }, [issues, drafts, dueStatus, uploadedDays, dueKey, dueWeekday]);

  /*
   * What the header should say about the day.
   *
   * Reported upward rather than worked out twice: the header states it, this
   * panel acts on it, and there is one place either of them can be wrong.
   */
  useEffect(() => {
    if (loading || !onDayStatus) return;

    const filed = ['Submitted', 'Approved'].includes(dueStatus.label);
    const salesIn = uploadedDays && uploadedDays.has(dueKey);

    onDayStatus([
      {
        tone: filed ? 'done' : dueStatus.label === 'Draft' ? 'doing' : 'todo',
        label: filed
          ? 'Banking filed'
          : dueStatus.label === 'Draft'
          ? 'Banking part-finished'
          : 'Banking not started',
      },
      {
        tone: salesIn ? 'done' : 'todo',
        label: salesIn ? 'Sales uploaded' : 'Sales not uploaded',
      },
      ...(issues.length
        ? [{ tone: 'todo', label: `${issues.length} variance${issues.length === 1 ? '' : 's'} to sign off` }]
        : []),
    ]);
    /* Deliberate deps: the three facts the header states, and nothing else. */
  }, [loading, onDayStatus, dueStatus, uploadedDays, dueKey, issues.length]);

  const nextAction = (() => {
    if (loading) return null;

    const issue = oldestBy(issues);
    if (issue) {
      return {
        text: `Resolve the cash variance from ${namedDay(issue)}`,
        cta: 'Review issue',
        tone: 'error',
        onClick: () => setDialog('issues'),
      };
    }

    const draft = oldestBy(drafts);
    if (draft) {
      return {
        text: `Finish the reconciliation for ${namedDay(draft)}`,
        cta: 'Continue',
        tone: 'warning',
        onClick: () => continueDraft(draft),
      };
    }

    if (dueStatus.label === 'Not started') {
      return {
        text: `${dueWeekday}’s banking and cash count has not been started`,
        cta: 'Start now',
        tone: 'warning',
        onClick: navigateToReconciliation,
      };
    }

    return null;
  })();

  const allIssues = submitted.filter(hadIssue);
  const monthlyIssues = allIssues.filter((row) => reconciliationDate(row).slice(0, 7) === issueMonth);
  const issueMonths = [...new Set(allIssues.map((row) => reconciliationDate(row).slice(0, 7)).filter(Boolean))]
    .sort((a, b) => b.localeCompare(a));
  const lists = { history: submitted, drafts, issues };

  const managerForSite = (site) => {
    const siteValue = String(site?._id || '');
    const assigned = users.find((candidate) => {
      if (String(candidate?.role || '').toLowerCase() !== 'site_manager') return false;
      const candidateSite = typeof candidate?.site === 'object'
        ? candidate.site?._id
        : candidate?.site;
      return String(candidateSite || '') === siteValue;
    });
    const populated = typeof site?.manager === 'object' ? site.manager : null;
    return assigned || populated || null;
  };

  const continueDraft = (row) => {
    const date = reconciliationDate(row) || new Date().toISOString().slice(0, 10);
    navigate(`/platform/site/${siteCode}/reconciliation?date=${encodeURIComponent(date)}`);
    setDialog('');
  };

  /*
   * The list carries summary fields only, so the full report is fetched here.
   *
   * The summary row is shown immediately and replaced when the document
   * arrives: the dialog opens at once with the date and the headline figures,
   * rather than waiting on a request before showing anything.
   */
  const openRecord = async (row) => {
    setSelectedRecord(row);
    setDialog('');

    if (!row?._id) return;

    try {
      const response = await reconciliationAPI.getOne(row._id);
      const full = response?.data?.data;

      // Ignore a late reply if the manager has since closed it or opened
      // another day.
      setSelectedRecord((current) =>
        full && current && String(current._id) === String(row._id) ? full : current
      );
    } catch (error) {
      // The summary row stays on screen; it carries the date, status and
      // variance, which is most of what the dialog shows.
      console.warn('[dashboard] could not load the full reconciliation', error);
    }
  };

  const discardDraft = async (row) => {
    if (!row?._id) return;
    const confirmed = window.confirm(
      `Discard the saved draft for ${reconciliationDate(row)}? This draft will be archived and cannot be continued.`
    );
    if (!confirmed) return;
    try {
      setDiscardingId(String(row._id));
      await reconciliationAPI.update(row._id, { status: 'discarded' });
      setRecords((current) => current.map((item) =>
        String(item?._id) === String(row._id) ? { ...item, status: 'discarded' } : item
      ));
    } finally {
      setDiscardingId('');
    }
  };

  /*
   * The three boxes under the job list.
   *
   * All of this was already loaded and none of it was on the page: the filed
   * days were a text link reading "Open a past reconciliation (23)", the open
   * variances existed only inside a dialog you reached by pressing Review on a
   * job, and the directory was a small grey button in a corner. The data is
   * shaped here and the panels are told nothing about the API.
   */
  const PREVIEW = 5;

  const shapeRow = (row) => ({
    id: String(row?._id || reconciliationDate(row)),
    raw: row,
    dateLabel: namedDay(row),
    status: String(row?.status || '').toLowerCase(),
    variance: openVarianceOf(row),
  });

  const pastRows = [...submitted]
    .sort((a, b) => reconciliationDate(b).localeCompare(reconciliationDate(a)))
    .slice(0, PREVIEW)
    .map(shapeRow);

  const issueRows = [...issues]
    .sort((a, b) => reconciliationDate(b).localeCompare(reconciliationDate(a)))
    .slice(0, PREVIEW)
    .map(shapeRow);

  const currentSite =
    sites.find(
      (site) =>
        String(site?._id || '') === String(siteId || '') ||
        String(site?.code || '').toUpperCase() === String(siteCode || '').toUpperCase()
    ) || null;

  const currentDirectory =
    SITE_DIRECTORY_DETAILS[String(currentSite?.code || siteCode || '').toUpperCase()] || {};

  const currentManager = currentSite ? managerForSite(currentSite) : null;

  const directorySite = {
    name: currentSite?.name || siteCode,
    code: currentSite?.code || siteCode,
    managerName:
      currentDirectory.managerName ||
      [currentManager?.firstName, currentManager?.lastName].filter(Boolean).join(' ') ||
      currentManager?.fullName ||
      '',
    email: currentDirectory.email || currentManager?.email || currentSite?.contact?.email || '',
    phone: currentDirectory.phone || currentSite?.contact?.phone || '',
  };

  const otherSites = sites
    .filter((site) => String(site?._id || '') !== String(currentSite?._id || 'none'))
    .map((site) => {
      const details = SITE_DIRECTORY_DETAILS[String(site?.code || '').toUpperCase()] || {};
      return {
        code: site?.code || '',
        name: site?.name || site?.code || 'Unnamed site',
        phone: details.phone || site?.contact?.phone || '',
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <Box sx={{ display: 'grid', gap: 1.5 }}>
      {/*
        Everything outstanding, in one place.

        This was three surfaces. "What needs doing" here, "Active Alerts" a
        screen further down, and a "Reconciliation Status" panel below that
        with its own sentence of guidance — and they overlapped, so the same
        unfinished reconciliation appeared in all three and a manager had to
        read the whole page to be sure they had seen everything.
      */}
      <PriorityPanel
        items={priorityItems}
        dueLabel={dueLabel}
        loading={loading}
      />

      {/* The week's figures, the fuel cover, the exceptions and the chart. */}
      {middle}

      {/*
        The records, at the foot of the page.

        Everything here is a look backwards: days already filed, variances
        already raised, numbers to ring. None of it is a job, and a job list
        that has to be scrolled past reference material to reach the figures
        it depends on reads in the wrong order. What is outstanding is at the
        top, what is happening now is in the middle, what has happened is here.
      */}
      <Grid container spacing={1.5} alignItems="stretch">
        <Grid item xs={12} md={4}>
          <PastReconciliationsPanel
            rows={pastRows}
            total={submitted.length}
            loading={loading}
            onOpen={(row) => openRecord(row.raw)}
            onViewAll={() => setDialog('history')}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <IssuesPanel
            rows={issueRows}
            loading={loading}
            onOpen={() => setDialog('issues')}
            onViewAll={() => setDialog('issues')}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <SiteDirectoryPanel
            site={directorySite}
            others={otherSites}
            loading={loading}
            onViewAll={() => setDialog('sites')}
          />
        </Grid>
      </Grid>
      </Box>

      <Dialog open={Boolean(dialog)} onClose={() => setDialog('')} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>
          {dialog === 'history' ? 'Submitted reconciliations' : dialog === 'drafts' ? 'Pending reconciliation drafts' : dialog === 'sites' ? 'Site directory' : 'Issues detected'}
        </DialogTitle>
        <DialogContent dividers>
          {dialog === 'sites' ? (
            <Grid container spacing={1.25}>
              {sites.map((site, index) => {
                const manager = managerForSite(site);
                const directory = SITE_DIRECTORY_DETAILS[String(site?.code || '').toUpperCase()] || {};
                const managerName =
                  directory.managerName ||
                  [manager?.firstName, manager?.lastName].filter(Boolean).join(' ') ||
                  manager?.fullName ||
                  'Manager not assigned';
                const isCurrent = String(site?._id || '') === String(siteId || '') || String(site?.code || '') === String(siteCode || '');
                return (
                  <Grid item xs={12} md={6} key={site?._id || site?.code || index}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        
                        borderWidth: isCurrent ? 2 : 1,
                        borderColor: isCurrent ? 'primary.main' : '#d7e5da',
                        bgcolor: isCurrent ? '#edf8f0' : '#fff',
                      }}
                    >
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" spacing={1}>
                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>{site?.name || 'Unnamed site'}</Typography>
                            <Typography variant="caption" color="text.secondary">Site code: {site?.code || '—'}</Typography>
                          </Box>
                          {isCurrent ? <Chip size="small" color="success" label="Logged-in site" /> : null}
                        </Stack>
                        <Divider sx={{ my: 1.25 }} />
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{managerName}</Typography>
                        <Typography variant="body2" color="text.secondary">{directory.email || manager?.email || site?.contact?.email || 'Email not available'}</Typography>
                        <Typography variant="body2" color="text.secondary">Site number: {directory.phone || site?.contact?.phone || 'Not available'}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : dialog === 'issues' ? (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.25} sx={{ mb: 1.5 }}>
                <Box>
                  <Typography sx={{ fontWeight: 900 }}>Issue audit history</Typography>
                  <Typography variant="body2" color="text.secondary">Open and resolved reconciliation issues, including who resolved them and why.</Typography>
                </Box>
                <TextField
                  select
                  size="small"
                  label="Month"
                  value={issueMonth}
                  onChange={(event) => setIssueMonth(event.target.value)}
                  sx={{ minWidth: 190 }}
                >
                  {[issueMonth, ...issueMonths].filter((value, index, array) => value && array.indexOf(value) === index).map((month) => (
                    <MenuItem key={month} value={month}>{new Date(`${month}-01T12:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack spacing={1}>
                {monthlyIssues.map((row) => {
                  const resolution = resolutionDetails(row);
                  const variance = Number(
                    row?.issueSummary?.cashVarianceIncludingSubway ??
                    row?.cashVarianceIncludingSubway ??
                    row?.issueSummary?.cashVarianceExcludingSubway ??
                    row?.cashVarianceExcludingSubway ??
                    row?.variances?.cashVariance ??
                    0
                  );
                  return (
                    <Card key={row?._id} variant="outlined" sx={{  borderColor: resolution.resolved ? '#b7dec2' : '#f1c0c0' }}>
                      <CardContent>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                          <Box>
                            <Typography sx={{ fontWeight: 900 }}>{new Date(`${reconciliationDate(row)}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</Typography>
                            <Typography variant="body2" color="text.secondary">Recorded variance: {new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(variance)}</Typography>
                          </Box>
                          <Chip size="small" color={resolution.resolved ? 'success' : 'error'} label={resolution.resolved ? 'Resolved' : 'Open'} />
                        </Stack>
                        {resolution.resolved ? (
                          <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: '#f1f8f3' }}>
                            {row?.issueResolution?.category ? (
                              <Typography variant="body2"><strong>Cause:</strong> {categoryLabel(row.issueResolution.category)}</Typography>
                            ) : null}
                            <Typography variant="body2"><strong>What happened:</strong> {resolution.reason || 'No resolution reason recorded'}</Typography>
                            {row?.issueResolution?.actionTaken ? (
                              <Typography variant="body2"><strong>Action taken:</strong> {row.issueResolution.actionTaken}</Typography>
                            ) : null}
                            {row?.issueResolution?.authorisedByName ? (
                              <Typography variant="body2"><strong>Authorised by:</strong> {row.issueResolution.authorisedByName}</Typography>
                            ) : null}
                            <Typography variant="body2"><strong>Recorded by:</strong> {resolution.person || 'Not recorded'}</Typography>
                            <Typography variant="body2"><strong>Resolved on:</strong> {resolution.date ? new Date(resolution.date).toLocaleString('en-GB') : 'Not recorded'}</Typography>
                          </Box>
                        ) : (
                          <ResolveIssueForm
                            row={row}
                            variance={variance}
                            onResolved={() => setIssueRefresh((k) => k + 1)}
                          />
                        )}

                        {/* Everything that has ever been claimed about this
                            variance, including sign-offs that were later
                            reopened. */}
                        {(row?.issueResolution?.history || []).length > 1 ? (
                          <Box sx={{ mt: 1.25 }}>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                              Audit trail
                            </Typography>
                            {(row.issueResolution.history || []).map((entry, i) => (
                              <Typography key={i} variant="caption" display="block" color="text.secondary">
                                {entry.action === 'reopened' ? 'Reopened' : 'Resolved'} by {entry.byName || 'Unknown'}
                                {entry.at ? ` on ${new Date(entry.at).toLocaleString('en-GB')}` : ''}
                                {entry.category ? ` — ${categoryLabel(entry.category)}` : ''}
                              </Typography>
                            ))}
                          </Box>
                        ) : null}

                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button size="small" variant="outlined" onClick={() => openRecord(row)}>View full report</Button>
                          {resolution.resolved ? (
                            <Button
                              size="small"
                              color="warning"
                              onClick={async () => {
                                await reconciliationAPI.reopenIssue(row?._id, {});
                                setIssueRefresh((k) => k + 1);
                              }}
                            >
                              Reopen
                            </Button>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                {!monthlyIssues.length ? (
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <Typography sx={{ fontWeight: 850 }}>No issues recorded for this month</Typography>
                    <Typography variant="body2" color="text.secondary">Choose another month to review earlier issue history.</Typography>
                  </Box>
                ) : null}
              </Stack>
            </Box>
          ) : (
            <List disablePadding>
              {(lists[dialog] || []).map((row, index) => (
                <React.Fragment key={row?._id || index}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      dialog === 'drafts' ? (
                        <Stack direction="row" spacing={0.75}>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<DeleteOutlineRounded />}
                            disabled={discardingId === String(row?._id)}
                            onClick={() => discardDraft(row)}
                          >
                            Discard
                          </Button>
                          <Button size="small" variant="contained" onClick={() => continueDraft(row)}>Continue</Button>
                        </Stack>
                      ) : (
                        <Button size="small" variant="outlined" startIcon={<DescriptionRounded />} onClick={() => openRecord(row)}>Full report</Button>
                      )
                    }
                  >
                    <ListItemButton onClick={() => dialog === 'drafts' ? continueDraft(row) : openRecord(row)}>
                      <ListItemText
                        primary={new Date(`${reconciliationDate(row)}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
                        secondary={`${String(row?.status || '').toUpperCase()}${row?.submittedByName ? ` • ${row.submittedByName}` : ''}`}
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < (lists[dialog] || []).length - 1 ? <Divider /> : null}
                </React.Fragment>
              ))}
              {!loading && !(lists[dialog] || []).length ? (
                <Box sx={{ py: 5, textAlign: 'center' }}>
                  <Typography sx={{ fontWeight: 850 }}>No records found</Typography>
                  <Typography variant="body2" color="text.secondary">There is nothing requiring action in this section.</Typography>
                </Box>
              ) : null}
            </List>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDialog('')}>Close</Button></DialogActions>
      </Dialog>

      <SubmittedReconciliationDialog
        open={Boolean(selectedRecord)}
        onClose={() => setSelectedRecord(null)}
        reconciliation={selectedRecord}
      />
    </>
  );
}

export default function SmartSiteDashboard() {
  const navigate = useNavigate();
  const { siteCode: routeSiteCode } = useParams();
  const { user } = useAuth();
  const { platform, siteName } = usePlatform();
  const currentWeek = useMemo(() => getWeekForDate(new Date()), []);
  const [week, setWeek] = useState(currentWeek);
  const siteId = useMemo(
    () =>
      platform?.siteId ||
      (typeof user?.site === 'object' ? user?.site?._id : user?.site) ||
      localStorage.getItem('selectedSiteId') ||
      '',
    [platform?.siteId, user?.site]
  );
  const siteCode =
    routeSiteCode ||
    platform?.siteCode ||
    localStorage.getItem('selectedSiteCode') ||
    '';

  const [totals, setTotals] = useState(emptyTotals);
  const [delta, setDelta] = useState(emptyTotals);
  const [goldenLinesSummary, setGoldenLinesSummary] = useState({});

const [outstandingCasesSummary, setOutstandingCasesSummary] = useState({
  count: 0,
  value: 0,
});

  /*
   * The Daily Sales response has always carried a days[] array alongside the
   * totals and it was being discarded. Keeping the dates costs nothing and is
   * what the week strip in the page header is drawn from.
   */
  const [uploadedDays, setUploadedDays] = useState(() => new Set());

const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  /*
   * How today's filing stands, reported up from the panel that works it out.
   *
   * The header states it and the panel beneath acts on it — one source, said
   * once in each place rather than computed twice and left to drift.
   */
  const [dayStatuses, setDayStatuses] = useState([]);

  const selectWeek = (weekStart) => {
    if (!weekStart) return;
    setWeek(getWeekForDate(new Date(`${weekStart}T12:00:00`)));
  };

  const moveWeek = (days) => {
    const date = new Date(`${week.start}T12:00:00`);
    date.setDate(date.getDate() + days);
    setWeek(getWeekForDate(date));
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const currentStart = new Date(`${week.start}T12:00:00`);
    const previousStart = new Date(currentStart);
    previousStart.setDate(previousStart.getDate() - 7);

    /*
     * Compare like with like.
     *
     * The previous range used to run the full seven days regardless of how much
     * of the selected week had actually happened, so on a Tuesday two days of
     * trade were measured against seven and every card reported a collapse. The
     * comparison now covers the same number of elapsed days, and only widens to
     * the whole week once the selected week is over.
     */
    const todayKey = isoDate(new Date());
    const elapsed = week.end <= todayKey
      ? 7
      : Math.min(
          7,
          Math.max(
            1,
            Math.round((new Date(`${todayKey}T12:00:00`) - currentStart) / 86400000) + 1
          )
        );

    const previousEnd = new Date(previousStart);
    previousEnd.setDate(previousEnd.getDate() + elapsed - 1);

   const results = await Promise.allSettled([
  dailySalesAPI.list({
    siteId,
    start: week.start,
    end: week.end,
  }),

  dailySalesAPI.list({
    siteId,
    start: isoDate(previousStart),
    end: isoDate(previousEnd),
  }),

  goldenLinesAPI.siteSummary({
    siteId,
    weekStart: week.start,
    start: week.start,
    weekEnd: week.end,
    end: week.end,
  }),

  reconciliationAPI.listExceptions({
  siteId,
  site: siteId,

  /*
   * Outstanding Cases represents every unresolved recovery case,
   * including cases created in an earlier week.
   */
  history: true,

  limit: 1000,
}),
]);

    const currentTotals = results[0].status === 'fulfilled'
      ? totalsFrom(results[0].value)
      : emptyTotals;

    const currentDays = results[0].status === 'fulfilled'
      ? (results[0].value?.data?.data?.days || results[0].value?.data?.days || [])
      : [];
    setUploadedDays(
      new Set(currentDays.map(recordDateKey).filter(Boolean))
    );
    const previousTotals = results[1].status === 'fulfilled'
      ? totalsFrom(results[1].value)
      : emptyTotals;
    setTotals(currentTotals);

    /*
     * Carry the previous period alongside the difference.
     *
     * A previous week with no uploads and a previous week that genuinely took
     * nothing both subtract to zero, so the delta arrived equal to the whole
     * current figure and rendered as "↑ £29,548" beside a value of £29,548 — a
     * comparison against nothing, drawn as growth. The cards need the other
     * side of the sum to tell those two cases apart, and to express the change
     * as a proportion rather than a repeat of the headline number.
     */
    setDelta({
      ...subtractTotals(currentTotals, previousTotals),
      comparableDays: Number(previousTotals?.daysFilled || 0),
      previous: {
        shopSales: Number(previousTotals?.shopSalesTotal || 0),
        fuelLitres: Object.values(previousTotals?.fuelLitres || {})
          .reduce((sum, value) => sum + Number(value || 0), 0),
        solar: Number(previousTotals?.solar?.sum || 0),
      },
    });

    if (results[2].status === 'fulfilled') {
      const body = results[2].value?.data?.data || results[2].value?.data || {};
      const units = Number(body?.totals?.totalQty || 0);
      const departments = Array.isArray(body?.departments) ? body.departments.length : 0;
      const hasReport = units > 0 || departments > 0;
      setGoldenLinesSummary({
        value: hasReport ? `${units.toLocaleString()} units` : 'Not reported',
        helper: hasReport ? `${departments} departments reported` : 'Weekly upload required',
        trend: hasReport ? 'View performance' : 'Upload now',
        tone: hasReport ? 'primary' : 'warning',
      });
    } else {
      setGoldenLinesSummary({
        value: 'Unavailable',
        helper: 'Could not check weekly report',
        trend: 'Try again',
        tone: 'warning',
      });
    }

   if (results[3].status === 'fulfilled') {
  const responseBody =
    results[3].value?.data?.data ??
    results[3].value?.data ??
    {};

  /*
   * The recovery endpoint may return either a direct array
   * or an object containing separate Drive Off and NMOP arrays.
   */
  const exceptionRows = (() => {
    if (Array.isArray(responseBody)) {
      return responseBody;
    }

    const directRows =
      responseBody?.rows ??
      responseBody?.items ??
      responseBody?.incidents ??
      responseBody?.exceptions ??
      responseBody?.records ??
      responseBody?.results;

    if (Array.isArray(directRows)) {
      return directRows;
    }

    if (Array.isArray(responseBody?.data)) {
      return responseBody.data;
    }

    const nestedRows =
      responseBody?.data?.rows ??
      responseBody?.data?.items ??
      responseBody?.data?.incidents ??
      responseBody?.data?.exceptions ??
      responseBody?.data?.records;

    if (Array.isArray(nestedRows)) {
      return nestedRows;
    }

    /*
     * Some responses keep Drive Off and NMOP cases separately.
     */
    const driveOffs = Array.isArray(responseBody?.driveOffs)
      ? responseBody.driveOffs
      : Array.isArray(responseBody?.data?.driveOffs)
        ? responseBody.data.driveOffs
        : [];

    const nmopCases = Array.isArray(responseBody?.nmop)
      ? responseBody.nmop
      : Array.isArray(responseBody?.nmops)
        ? responseBody.nmops
        : Array.isArray(responseBody?.noMeansOfPayment)
          ? responseBody.noMeansOfPayment
          : Array.isArray(responseBody?.data?.nmop)
            ? responseBody.data.nmop
            : Array.isArray(responseBody?.data?.nmops)
              ? responseBody.data.nmops
              : [];

    return [...driveOffs, ...nmopCases];
  })();

  const normaliseStatus = (value) =>
    String(value || 'open')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_');

  const resolvedStatuses = new Set([
    'paid',
    'paid_in_full',
    'recovered',
    'resolved',
    'settled',
    'closed',
    'written_off',
    'cancelled',
  ]);

  const unresolvedCases = exceptionRows.filter((row) => {
    const status = normaliseStatus(
      row?.workflowStatus ??
        row?.caseStatus ??
        row?.recoveryStatus ??
        row?.status
    );

    return !resolvedStatuses.has(status);
  });

  const outstandingValue = unresolvedCases.reduce(
    (total, row) => {
      const amount =
        row?.outstandingAmount ??
        row?.outstanding ??
        row?.remainingBalance ??
        row?.remainingAmount ??
        row?.balance ??
        row?.amountOutstanding ??
        row?.originalLoss ??
        row?.lossAmount ??
        row?.amount ??
        0;

      const numericAmount = Number(amount);

      return (
        total +
        (Number.isFinite(numericAmount)
          ? Math.abs(numericAmount)
          : 0)
      );
    },
    0
  );

  setOutstandingCasesSummary({
    count: unresolvedCases.length,
    value: Number(outstandingValue.toFixed(2)),
  });

  /*
   * Keep this temporarily while testing.
   * It will show the exact API structure in the browser console.
   */
 
} else {
  console.error(
    '[Dashboard outstanding cases] Request failed:',
    results[3].reason
  );

  setOutstandingCasesSummary({
    count: 0,
    value: 0,
  });
}

    if (results[0].status === 'rejected') {
      setError('Daily Sales totals could not be loaded. The rest of the operational dashboard is still available.');
    }
    setLoading(false);
  }, [siteId, week.end, week.start]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Box className="vf-responsive-page vf-dashboard-page" sx={{ width: '100%', maxWidth: 'none', mx: 0, display: 'grid', gap: 1.5 }}>
      {/*
        The page header.

        It said "Weekly Operations Overview — live priorities, performance and
        completion status for your site", which describes a page rather than
        stating anything about the business. Nothing in it was specific to the
        day, the site or the work, so the eye skipped it and went hunting three
        sections down for whether yesterday had been filed. It now carries that
        answer itself.
      */}
      <DashboardMasthead
        siteName={siteName}
        siteCode={siteCode}
        todayLabel={new Date().toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
        dueLabel={new Date(`${tradingDay()}T12:00:00`).toLocaleDateString('en-GB', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}
        statuses={dayStatuses}
        weekLabel={formatWeek(week.start, week.end)}
        weekStart={week.start}
        onWeekStep={moveWeek}
        onWeekPick={selectWeek}
        onRefresh={() => setRefreshKey((key) => key + 1)}
        refreshing={loading}
      />

      {error ? <Alert severity="warning">{error}</Alert> : null}
      {loading && !totals?.daysFilled ? <Skeleton variant="rounded" height={132} /> : null}

      <RecordsAndFollowUp
        siteId={siteId}
        siteCode={siteCode}
        refreshKey={refreshKey}
        uploadedDays={uploadedDays}
        onDayStatus={setDayStatuses}
        middle={
          /* Reserve the height so the page does not jump when the charts land. */
          <Suspense fallback={<Skeleton variant="rounded" height={420} />}>
            <SmartOperationsDashboard
              sites={[{ _id: siteId, active: true }]}
              siteId={siteId}
              weekStart={week.start}
              weekEnd={week.end}
              totals={totals}
              delta={delta}
              basePath={`/platform/site/${siteCode}`}
              goldenLinesSummary={goldenLinesSummary}
              outstandingCasesSummary={outstandingCasesSummary}
              coverageStrip={
                <WeekUploadStrip
                  weekStart={week.start}
                  uploadedDays={uploadedDays}
                  dueKey={tradingDay()}
                  loading={loading}
                  onOpenDay={(day) =>
                    navigate(`/platform/site/${siteCode}/daily-sales?start=${day}&end=${day}`)
                  }
                />
              }
            />
          </Suspense>
        }
      />
    </Box>
  );
}
